import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, Marker, Popup, useMap } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery } from '@apollo/client';
import { FETCH_ALL_SHIFTS, FETCH_SHIFT } from '../../../api/mutations/ShiftManagementMutations';
import { ShiftTemplateFilter } from './ShiftTemplateFilter';
import { stagingOptions } from '../../../constants/apollo';
import { Shift, Driver, Assignment, AssignmentStatus, ShiftTemplate } from '../types';
import { getStatusColor, formatDateTime, formatTime, formatDate, formatDateToDDMMYY } from '../utils';
import DatePickerDDMMYY from '../../../components/common/DatePickerDDMMYY';
import {
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Map as MapIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Tooltip, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { centerLat, centerLng, zoom } from '../../../constants/map';
import { MapStyleRetro } from '../../../constants/enums';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createDriverIcon = (status: string, isSelected: boolean = false) => {
  const getColor = () => {
    switch (status) {
      case 'Online':
        return '#10B981';
      case 'Busy':
        return '#F59E0B';
      case 'Idle':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  const size = isSelected ? 28 : 22;
  const borderWidth = isSelected ? 3 : 2;

  return L.divIcon({
    className: 'custom-driver-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${getColor()};
        border: ${borderWidth}px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: ${size - 8}px;
          height: ${size - 8}px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

interface MapBoundsUpdaterProps {
  drivers: Driver[];
  selectedDriverId: string | null;
  openPopupDriverId: string | null;
}

const MapBoundsUpdater: React.FC<MapBoundsUpdaterProps> = ({ drivers, selectedDriverId, openPopupDriverId }) => {
  const map = useMap();
  const markerRefs = useRef<{ [key: string]: any }>({});

  useEffect(() => {
    if (selectedDriverId && openPopupDriverId) {
      const selectedDriver = drivers.find(d => d.id === selectedDriverId);
      if (selectedDriver && selectedDriver.latitude != null && selectedDriver.longitude != null) {
        map.setView([selectedDriver.latitude, selectedDriver.longitude], 15, {
          animate: true,
          duration: 0.5
        });

        setTimeout(() => {
          const marker = markerRefs.current[selectedDriverId];
          if (marker) {
            marker.openPopup();
          }
        }, 600);
      }
    } else if (drivers.length > 0 && drivers.some(d => d.latitude != null && d.longitude != null) && !selectedDriverId) {
      const bounds = L.latLngBounds(
        drivers
          .filter(d => d.latitude != null && d.longitude != null)
          .map(d => [d.latitude!, d.longitude!] as [number, number])
      );
      if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [drivers, map, selectedDriverId, openPopupDriverId]);

  return null;
};

const MarkerWithRef: React.FC<{
  driver: Driver;
  selectedDriverId: string | null;
  openPopupDriverId: string | null;
  onMarkerClick: (driverId: string) => void;
  markerRefs: React.MutableRefObject<{ [key: string]: any }>;
  shiftAssignments: Assignment[];
  activeShifts: Shift[];
}> = ({ driver, selectedDriverId, openPopupDriverId, onMarkerClick, markerRefs, shiftAssignments, activeShifts }) => {
  const markerRef = useRef<any>(null);

  useEffect(() => {
    if (markerRef.current) {
      markerRefs.current[driver.id] = markerRef.current;
    }
  }, [driver.id, markerRefs]);

  const assignment = shiftAssignments.find(a => a.driverId === driver.id);
  const shift = assignment ? activeShifts.find(s => s.id === assignment.shiftId) : null;
  const assignmentStatus: AssignmentStatus = assignment?.status || 'pending';

  // Don't render marker if coordinates are null
  if (driver.latitude == null || driver.longitude == null) {
    return null;
  }

  return (
    <Marker
      ref={markerRef}
      position={[driver.latitude, driver.longitude]}
      icon={createDriverIcon(driver.status, selectedDriverId === driver.id)}
      eventHandlers={{
        click: () => onMarkerClick(driver.id),
      }}
    >
      <Popup>
        <div className="p-1.5 min-w-[220px]">
          <div className="flex items-center justify-between mb-1.5 gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <h3 className="font-semibold text-gray-900 text-xs">{driver.name}</h3>
                <span
                  className={`px-1 py-0.5 text-[9px] font-semibold rounded border flex-shrink-0 ${getStatusColor(
                    driver.status
                  )}`}
                >
                  {driver.status}
                </span>
                {assignment && (
                  <span
                    className={`px-1 py-0.5 text-[8px] font-medium rounded border flex-shrink-0 ${getStatusColor(assignmentStatus)}`}
                  >
                    {assignmentStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-[10px] text-gray-600">
                <PhoneIcon sx={{ fontSize: '10px' }} />
                <span>{driver.phone}</span>
              </div>
            </div>
          </div>

          {shift && assignment && (
            <div className="mb-1.5 pb-1.5 border-b border-gray-200/50">
              <div className="flex items-center gap-1 mb-0.5">
                <ScheduleIcon sx={{ fontSize: '10px', color: '#6B7280' }} />
                <span className="text-[9px] font-semibold text-gray-900">{shift.name}</span>
              </div>
              {shift.zone && (
                <span className="text-[9px] text-gray-600 mr-2">{shift.zone}</span>
              )}
              <span className="text-[9px] text-gray-500">
                {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
              </span>
            </div>
          )}

          {assignment && (
            <div className="mb-1.5 pb-1.5 border-b border-gray-200/50 space-y-0.5">
              {assignment.createdAt && (
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-gray-500">Assigned:</span>
                  <span className="text-[9px] text-gray-700">{formatDate(assignment.createdAt)}</span>
                </div>
              )}
              {assignment.updatedAt && assignment.updatedAt !== assignment.createdAt && (
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-gray-500">Updated:</span>
                  <span className="text-[9px] text-gray-700">{formatDate(assignment.updatedAt)}</span>
                </div>
              )}
            </div>
          )}

          {driver.latitude != null && driver.longitude != null ? (
          <div className="flex items-center gap-1">
            <LocationIcon sx={{ fontSize: '10px', color: '#6B7280' }} />
            <span className="text-[9px] text-gray-900">
                {driver.latitude.toFixed(4)}, {driver.longitude.toFixed(4)}
            </span>
          </div>
          ) : (
            <div className="flex items-center gap-1">
              <LocationIcon sx={{ fontSize: '10px', color: '#EF4444' }} />
              <span className="text-[9px] text-red-600 italic">Location not available</span>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

interface LiveMonitorViewProps {
  drivers: Driver[];
  assignments: any[];
}

export const LiveMonitorView: React.FC<LiveMonitorViewProps> = ({
  drivers,
  assignments,
}) => {
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [openPopupDriverId, setOpenPopupDriverId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    shiftSearch: '',
    driverStatus: '',
    driverSearch: '',
    zone: '',
    shiftTemplateId: '',
    startDate: '',
    endDate: '',
    activeNow: true,
  });
  const markerRefs = useRef<{ [key: string]: any }>({});
  const isFetchingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const SHIFT_LIMIT = 200;
  const [shiftOffset, setShiftOffset] = useState(0);
  const [allShifts, setAllShifts] = useState<Shift[]>([]);

  const [shiftDrivers, setShiftDrivers] = useState<Driver[]>([]);
  const [shiftAssignments, setShiftAssignments] = useState<Assignment[]>([]);
  const menuListRef = useRef<HTMLDivElement>(null);

  const [debouncedSearch, setDebouncedSearch] = useState(filters.shiftSearch);
  const [templateNameCache, setTemplateNameCache] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(filters.shiftSearch);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters.shiftSearch]);

  useEffect(() => {
    if (debouncedSearch !== filters.shiftSearch) return; 

    setAllShifts([]);
    setShiftOffset(0);
  }, [debouncedSearch]);

  useEffect(() => {
    setAllShifts([]);
    setShiftOffset(0);
  }, [filters.shiftTemplateId]);

  useEffect(() => {
    setAllShifts([]);
    setShiftOffset(0);
  }, [filters.startDate, filters.endDate]);

  const { data: shiftsData, loading: shiftsLoading, fetchMore } = useQuery(FETCH_ALL_SHIFTS, {
    ...stagingOptions,
    variables: {
      limit: SHIFT_LIMIT,
      offset: 0,
      status: 'ACTIVE' as any,
      search: debouncedSearch && debouncedSearch.trim() ? debouncedSearch.trim() : undefined,
      shiftTemplateId: filters.shiftTemplateId || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      activeNow: filters.activeNow ? true : undefined,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.fetchAllShifts?.items) {
        const fetchedShifts: Shift[] = data.fetchAllShifts.items.map((item: any) => {
          // Format name: "Name - ID" if name exists, otherwise "Shift - ID"
          const displayName = item.name && item.name.trim()
            ? `${item.name} - ${item.id}`
            : `Shift - ${item.id}`;
          return {
            id: item.id,
            name: displayName,
            startTime: item.startTime,
            endTime: item.endTime,
            requiredDrivers: item.requiredDrivers || 0,
            maxDrivers: item.requiredDrivers || 0,
            status: (item.status?.toLowerCase() || 'active') as any,
            zone: item.neighborhood?.name || item.city?.name || '',
            cityId: item.city?.id,
            neighborhoodId: item.neighborhood?.id,
            assignedDriversCount: item.assignedDriversCount || 0,
            availableSlots: item.availableSlots || 0,
          };
        });
        setAllShifts(fetchedShifts);
        setShiftOffset(SHIFT_LIMIT);
      } else {
        setAllShifts([]);
        setShiftOffset(0);
      }
    },
  });

  const hasMoreShifts = useMemo(() => {
    if (!shiftsData?.fetchAllShifts) return false;
    const totalCount = shiftsData.fetchAllShifts.totalCount || 0;
    return totalCount > 200 && allShifts.length < totalCount;
  }, [shiftsData, allShifts.length]);

  const loadMoreShifts = async () => {
    if (isFetchingMoreRef.current || !hasMoreShifts || shiftsLoading) return;

    isFetchingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          limit: SHIFT_LIMIT,
          offset: shiftOffset,
          status: 'ACTIVE' as any,
          search: debouncedSearch && debouncedSearch.trim() ? debouncedSearch.trim() : undefined,
          shiftTemplateId: filters.shiftTemplateId || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          activeNow: filters.activeNow ? true : undefined,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.fetchAllShifts) return prev;

          const newShifts: Shift[] = fetchMoreResult.fetchAllShifts.items.map((item: any) => {
            const displayName = item.name && item.name.trim()
              ? `${item.name} - ${item.id}`
              : `Shift - ${item.id}`;
            return {
              id: item.id,
              name: displayName,
              startTime: item.startTime,
              endTime: item.endTime,
              requiredDrivers: item.requiredDrivers || 0,
              maxDrivers: item.requiredDrivers || 0,
              status: (item.status?.toLowerCase() || 'active') as any,
              zone: item.neighborhood?.name || item.city?.name || '',
              cityId: item.city?.id,
              neighborhoodId: item.neighborhood?.id,
              assignedDriversCount: item.assignedDriversCount || 0,
              availableSlots: item.availableSlots || 0,
            };
          });

          const existingIds = new Set(allShifts.map(s => s.id));
          const uniqueNewShifts = newShifts.filter(s => !existingIds.has(s.id));

          setAllShifts(prev => [...prev, ...uniqueNewShifts]);
          setShiftOffset(prev => prev + SHIFT_LIMIT);

          return {
            ...fetchMoreResult,
            fetchAllShifts: {
              ...fetchMoreResult.fetchAllShifts,
              items: [...prev.fetchAllShifts.items, ...fetchMoreResult.fetchAllShifts.items],
            },
          };
        },
      });
    } catch (error) {
      console.error('Failed to load more shifts:', error);
    } finally {
      isFetchingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  };

  const handleDriverClick = (driverId: string) => {
    setSelectedDriverId(selectedDriverId === driverId ? null : driverId);
    setOpenPopupDriverId(selectedDriverId === driverId ? null : driverId);
  };

  const activeShifts = allShifts;

  useQuery(FETCH_SHIFT, {
    ...stagingOptions,
    variables: { id: selectedShiftId || '' },
    skip: !selectedShiftId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.fetchShift?.shiftAssignments) {
        const assignmentsFromAPI: Assignment[] = data.fetchShift.shiftAssignments.map((assignment: any) => ({
          id: assignment.id,
          shiftId: selectedShiftId || '',
          driverId: assignment.courier?.id || '',
          courierId: assignment.courier?.id || '',
          status: (assignment.status?.toLowerCase() || 'pending') as AssignmentStatus,
          createdAt: undefined,
          updatedAt: undefined,
          assignedAt: undefined,
          courier: assignment.courier ? {
            id: assignment.courier.id,
            firstName: assignment.courier.firstName,
            lastName: assignment.courier.lastName,
            mobileNumber: assignment.courier.mobileNumber,
            latitude: assignment.courier.latitude,
            longitude: assignment.courier.longitude,
            isOnline: assignment.courier.isOnline,
            isBusy: assignment.courier.isBusy,
          } : undefined,
        }));
        setShiftAssignments(assignmentsFromAPI);

        const driverIds = assignmentsFromAPI.map(a => a.courierId).filter(Boolean);
        const driversFromShift = driverIds.map(courierId => {
          const existingDriver = drivers.find(d => d.id === courierId);
          const assignment = assignmentsFromAPI.find(a => a.courierId === courierId);
          const courier = assignment?.courier;

          if (existingDriver) {
            return existingDriver;
          }

          // Determine status based on isOnline and isBusy
          let driverStatus: Driver['status'] = 'Idle';
          if (courier?.isOnline) {
            driverStatus = courier.isBusy ? 'Busy' : 'Online';
          }

          return {
            id: courierId,
            firstName: courier?.firstName || '',
            lastName: courier?.lastName || '',
            name: courier?.firstName && courier?.lastName
              ? `${courier.firstName} ${courier.lastName}`
              : courier?.firstName || courier?.lastName || 'Unknown',
            mobileNumber: courier?.mobileNumber || '',
            phone: courier?.mobileNumber || '',
            status: driverStatus,
            latitude: courier?.latitude,
            longitude: courier?.longitude,
            avatarUrl: undefined,
            rating: undefined,
            isOnline: courier?.isOnline,
            isBusy: courier?.isBusy,
            vehicleType: undefined,
            isBanned: false,
            lastSeen: undefined,
          } as Driver;
        });
        setShiftDrivers(driversFromShift);
      } else {
        setShiftAssignments([]);
        setShiftDrivers([]);
      }
    },
  });

  const selectedShift = selectedShiftId ? activeShifts.find(s => s.id === selectedShiftId) : null;

  const driversToShow = React.useMemo(() => {
    if (selectedShiftId && shiftDrivers.length > 0) {
      let filtered = shiftDrivers;

      if (filters.driverStatus) {
        if (filters.driverStatus === 'Online') {
          filtered = filtered.filter(d => d.isOnline === true);
        } else if (filters.driverStatus === 'Busy') {
          filtered = filtered.filter(d => d.isBusy === true);
        }
      }
      if (filters.driverSearch) {
        const searchLower = filters.driverSearch.toLowerCase();
        filtered = filtered.filter(d =>
          d.name.toLowerCase().includes(searchLower) ||
          d.id.toLowerCase().includes(searchLower) ||
          d.phone.includes(searchLower)
        );
      }
      if (filters.zone) {
        filtered = filtered.filter(d => {
          if (selectedShift && selectedShift.zone) {
            return selectedShift.zone === filters.zone;
          }
          return false;
        });
      }

      return filtered;
    }
    return [];
  }, [selectedShiftId, shiftDrivers, filters, selectedShift]);

  const totalActiveDrivers = driversToShow.length;
  const onlineCount = driversToShow.filter(d => d.isOnline === true && d.isBusy !== true).length;
  const offlineCount = driversToShow.filter(d => d.isOnline === false).length;
  const busyCount = driversToShow.filter(d => d.isBusy === true).length;

  const availableZones = React.useMemo(() => {
    return Array.from(new Set(activeShifts.map(s => s.zone))).sort();
  }, [activeShifts]);

  const shiftOptions = React.useMemo(() => {
    return activeShifts.map((shift) => ({
      value: shift.id,
      label: `${shift.name} - ${shift.zone || 'No Zone'}`,
      shift: shift,
    }));
  }, [activeShifts]);

  const selectedShiftOption = React.useMemo(() => {
    if (!selectedShiftId) return null;
    return shiftOptions.find(opt => opt.value === selectedShiftId) || null;
  }, [selectedShiftId, shiftOptions]);


  return (
    <div className="main-wrapper" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <div className="flex items-center justify-between mb-4 px-1 flex-shrink-0">
        <div>
          <h2 className="pageTitle mb-1 text-xl font-bold">Live Monitor</h2>
          <p className="text-sm text-gray-500">Real-time tracking of shifts and driver locations</p>
        </div>
      </div>


      <div className="space-y-4 mb-4 flex-shrink-0">

        {selectedShift && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <Tooltip title="Total number of active drivers in the selected shift" arrow>
                <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all cursor-help">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
                    <PeopleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Total Drivers</p>
                    <p className="text-xl font-bold text-gray-900">{totalActiveDrivers}</p>
                  </div>
                </div>
              </Tooltip>

              <Tooltip title="Drivers currently available and ready for assignments" arrow>
                <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all cursor-help">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
                    <CheckCircleIcon sx={{ fontSize: '20px', color: '#10B981' }} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Online</p>
                    <p className="text-xl font-bold text-gray-900">{onlineCount}</p>
                  </div>
                </div>
              </Tooltip>

              <Tooltip title="Drivers currently on a trip or busy" arrow>
                <div className="bg-gradient-to-br from-white to-amber-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all cursor-help">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center shadow-sm">
                    <CarIcon sx={{ fontSize: '20px', color: '#F59E0B' }} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Busy</p>
                    <p className="text-xl font-bold text-gray-900">{busyCount}</p>
                  </div>
                </div>
              </Tooltip>

              <Tooltip title="Drivers currently offline" arrow>
                <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all cursor-help">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-sm">
                    <AccessTimeIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Offline</p>
                    <p className="text-xl font-bold text-gray-900">{offlineCount}</p>
                  </div>
                </div>
              </Tooltip>
            </div>
          </>
        )}

        <div className="bg-gradient-to-r from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm px-4 py-3 mb-4">
          <div className="flex items-center flex-wrap gap-3">
            <div className="flex items-center gap-2 mr-2">
              <FilterIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
              <span className="text-xs font-semibold text-gray-700">Filters:</span>
              {shiftsLoading && (
                <Tooltip title="Loading shifts..." arrow placement="top">
                  <CircularProgress size={14} sx={{ color: '#3B82F6' }} />
                </Tooltip>
              )}
              <Tooltip
                title="Use filters to search shifts or narrow down drivers by status, search term, or zone. Active filters appear as removable tags."
                arrow
                placement="top"
              >
                <HelpIcon sx={{ fontSize: '14px', color: '#9CA3AF', cursor: 'help' }} />
              </Tooltip>
            </div>
            <ShiftTemplateFilter
              value={filters.shiftTemplateId || undefined}
              onChange={(value) => {
                const templateId = value ? value : '';
                setFilters((prev) => ({ ...prev, shiftTemplateId: templateId }));
              }}
                  placeholder="All Shift Schedulers"
              tooltipTitle="Filter shifts by Shift Scheduler"
              minWidth="200px"
              onTemplateNameChange={(templateId, templateName) => {
                if (templateName) {
                  setTemplateNameCache((prev) => ({ ...prev, [templateId]: templateName }));
                }
                  }}
                />

            <Tooltip title="Search shifts by name, ID, zone, or time" arrow placement="top">
              <div className="relative">
                <SearchIcon sx={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#9CA3AF' }} />
                <input
                  type="text"
                  value={filters.shiftSearch}
                  onChange={(e) => setFilters({ ...filters, shiftSearch: e.target.value })}
                  placeholder="Search shifts..."
                  className="h-9 pl-8 pr-3 text-xs border border-gray-300/80 rounded-[5px] focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none w-48 bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
                />
              </div>
            </Tooltip>

            <DatePickerDDMMYY
              value={filters.startDate}
              onChange={(value) => setFilters({ ...filters, startDate: value })}
              placeholder="Start Date: dd/mm/yy"
              tooltip="Filter shifts by start date. Click to open calendar picker."
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  height: '36px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '5px',
                  '& fieldset': {
                    borderRadius: '5px',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  },
                },
              }}
            />

            <DatePickerDDMMYY
              value={filters.endDate}
              onChange={(value) => setFilters({ ...filters, endDate: value })}
              placeholder="End Date: dd/mm/yy"
              tooltip="Filter shifts by end date. Click to open calendar picker. Must be after start date."
              minDate={filters.startDate}
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  height: '36px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '5px',
                  '& fieldset': {
                    borderRadius: '5px',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  },
                },
              }}
            />

            <Tooltip title="Show only shifts that are currently active (ongoing)" arrow placement="top">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.activeNow === true}
                    onChange={(e) => setFilters({ ...filters, activeNow: e.target.checked })}
                    size="small"
                    sx={{
                      color: '#3B82F6',
                      '&.Mui-checked': {
                        color: '#3B82F6',
                      },
                    }}
                  />
                }
                label={
                  <span className="text-xs font-medium text-gray-700">
                    Active Now (Only)
                  </span>
                }
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.75rem',
                  },
                }}
              />
            </Tooltip>

            {selectedShift && (
              <Tooltip title="Filter drivers by their current status: Online (available) or Busy (on trip)" arrow placement="top">
                <select
                  value={filters.driverStatus}
                  onChange={(e) => setFilters({ ...filters, driverStatus: e.target.value })}
                  className="h-9 px-3 text-xs border border-gray-300/80 rounded-[5px] focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
                >
                  <option value="">All Status</option>
                  <option value="Online">Online</option>
                  <option value="Busy">Busy</option>
                </select>
              </Tooltip>
            )}


            {selectedShift && (
              <Tooltip title="Search drivers by name, ID, or phone number" arrow placement="top">
                <div className="relative">
                  <SearchIcon sx={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#9CA3AF' }} />
                  <input
                    type="text"
                    value={filters.driverSearch}
                    onChange={(e) => setFilters({ ...filters, driverSearch: e.target.value })}
                    placeholder="Search driver..."
                    className="h-9 pl-8 pr-3 text-xs border border-gray-300/80 rounded-[5px] focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none w-40 bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
                  />
                </div>
              </Tooltip>
            )}
            {selectedShift && (
              <Tooltip title="Filter drivers by their assigned zone" arrow placement="top">
                <select
                  value={filters.zone}
                  onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
                  className="h-9 px-3 text-xs border border-gray-300/80 rounded-[5px] focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
                >
                  <option value="">All Zones</option>
                  {availableZones.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </Tooltip>
            )}

            {filters.shiftSearch && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <SearchIcon sx={{ fontSize: '12px' }} />
                <span>{filters.shiftSearch}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, shiftSearch: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.driverStatus && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <CheckCircleIcon sx={{ fontSize: '12px' }} />
                <span>{filters.driverStatus}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, driverStatus: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.shiftTemplateId && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '12px' }} />
                <span>
                  {(() => {
                    const name = templateNameCache[filters.shiftTemplateId] || `Template ${filters.shiftTemplateId}`;
                    return `${name} (${filters.shiftTemplateId})`;
                  })()}
                </span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, shiftTemplateId: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.driverSearch && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <SearchIcon sx={{ fontSize: '12px' }} />
                <span>{filters.driverSearch}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, driverSearch: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.zone && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <LocationIcon sx={{ fontSize: '12px' }} />
                <span>{filters.zone}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, zone: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.startDate && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '12px' }} />
                <span>From: {formatDateToDDMMYY(filters.startDate)}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, startDate: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.endDate && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '12px' }} />
                <span>To: {formatDateToDDMMYY(filters.endDate)}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, endDate: '' })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.activeNow && (
              <div className="inline-flex items-center gap-1.5 h-9 px-2.5 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <CheckCircleIcon sx={{ fontSize: '12px' }} />
                <span>Active Now</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => setFilters({ ...filters, activeNow: false })}
                    className="ml-0.5 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {(filters.shiftSearch || filters.driverStatus || filters.driverSearch || filters.zone || filters.shiftTemplateId || filters.startDate || filters.endDate || filters.activeNow) && (
              <Tooltip title="Remove all active filters" arrow placement="top">
                <button
                  onClick={() => {
                    setFilters({ shiftSearch: '', driverStatus: '', driverSearch: '', zone: '', shiftTemplateId: '', startDate: '', endDate: '', activeNow: false });
                  }}
                  className="ml-auto h-9 px-3 text-xs text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100/80 rounded-lg transition-all shadow-sm hover:shadow"
                >
                  Clear All
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        <div className="w-[360px] flex-shrink-0 flex flex-col gap-3 min-h-0 overflow-hidden">
          {selectedShift ? (
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200/80 shadow-sm p-5 flex-shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 truncate">{selectedShift.name}</h3>
                    <span className="px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full border border-emerald-200/60 flex-shrink-0">
                      Active
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2.5 text-sm text-gray-600">
                      <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="truncate">{selectedShift.zone}</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-sm text-gray-500">
                      <AccessTimeIcon sx={{ fontSize: '16px', color: '#9CA3AF' }} />
                      <span className="truncate">{formatDateTime(selectedShift.startTime)} - {formatDateTime(selectedShift.endTime)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip title="This shift is currently active and drivers are being monitored" arrow>
                    <InfoIcon sx={{ fontSize: '18px', color: '#9CA3AF', cursor: 'help', flexShrink: 0 }} />
                  </Tooltip>
                  <button
                    onClick={() => setSelectedShiftId(null)}
                    className="p-1.5 rounded-md text-white bg-red-500 hover:bg-red-600 transition-all shadow-sm hover:shadow flex items-center justify-center flex-shrink-0"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-white to-blue-50/20 rounded-lg border border-gray-200/60 shadow-sm flex flex-col min-h-0">
              <div className="px-3 py-2 border-b border-gray-200/60 bg-gradient-to-r from-gray-50/50 to-transparent">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-bold text-gray-900">Select a Shift Session</h3>
                    <Tooltip title="Choose a shift session to monitor its assigned drivers on the map. Click any shift session card to view drivers." arrow>
                      <HelpIcon sx={{ fontSize: '12px', color: '#9CA3AF', cursor: 'help' }} />
                    </Tooltip>
                  </div>
                  {shiftsLoading && (
                    <div className="flex items-center gap-1.5">
                      <CircularProgress size={12} sx={{ color: '#3B82F6' }} />
                      <span className="text-[10px] text-gray-500">Loading...</span>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">Click a shift to view its drivers</p>
              </div>
              <div
                ref={menuListRef}
                className="flex-1 overflow-y-auto p-4"
              >
                {shiftsLoading && allShifts.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-2">
                      <CircularProgress size={24} sx={{ color: '#3B82F6' }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700 mb-0.5">Loading shifts...</p>
                    <p className="text-[10px] text-gray-500">Please wait while we fetch active shifts</p>
                  </div>
                ) : activeShifts.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-2">
                      <ScheduleIcon sx={{ fontSize: '24px', color: '#9CA3AF' }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700 mb-0.5">
                      {filters.shiftSearch ? 'No shifts found' : 'No active shifts'}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {filters.shiftSearch
                        ? 'Try adjusting your search query'
                        : 'There are currently no active shifts to monitor'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {shiftsLoading && allShifts.length > 0 && (
                      <div className="flex items-center justify-center gap-2 py-2 px-3 bg-blue-50/50 border border-blue-200/50 rounded-lg mb-2">
                        <CircularProgress size={14} sx={{ color: '#3B82F6' }} />
                        <span className="text-xs text-gray-700 font-medium">Refreshing shifts...</span>
                      </div>
                    )}
                    {activeShifts.map((shift) => {
                      const assignedCount = shift.assignedDriversCount || 0;
                      const maxDrivers = shift.requiredDrivers || shift.maxDrivers || 0;
                      const progressPercentage = maxDrivers > 0 ? (assignedCount / maxDrivers) * 100 : 0;

                      const tooltipContent = (
                        <div className="p-2 space-y-1.5 min-w-[200px]">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-300">Shift ID:</span>
                            <span className="text-xs font-mono text-white">{shift.id}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-300">Name:</span>
                            <span className="text-xs text-white">{shift.name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-300">Zone:</span>
                            <span className="text-xs text-white">{shift.zone || 'N/A'}</span>
                          </div>
                          <div className="flex items-start justify-between">
                            <span className="text-xs font-semibold text-gray-300">Start:</span>
                            <span className="text-xs text-white text-right">{formatDateTime(shift.startTime)}</span>
                          </div>
                          <div className="flex items-start justify-between">
                            <span className="text-xs font-semibold text-gray-300">End:</span>
                            <span className="text-xs text-white text-right">{formatDateTime(shift.endTime)}</span>
                          </div>
                          <div className="flex items-center justify-between pt-1 border-t border-gray-600">
                            <span className="text-xs font-semibold text-gray-300">Capacity:</span>
                            <span className="text-xs font-bold text-white">{assignedCount} / {maxDrivers}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-300">Status:</span>
                            <span className="text-xs text-green-400 font-semibold">Active</span>
                          </div>
                        </div>
                      );

                      return (
                        <Tooltip
                          key={shift.id}
                          title={tooltipContent}
                          arrow
                          placement="right"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(17, 24, 39, 0.95)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '8px',
                                padding: 0,
                                maxWidth: 'none',
                              }
                            },
                            arrow: {
                              sx: {
                                color: 'rgba(17, 24, 39, 0.95)',
                              }
                            }
                          }}
                        >
                          <div
                            onClick={() => setSelectedShiftId(shift.id)}
                            className={`group p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${selectedShiftId === shift.id
                              ? 'border-primaryColor bg-primaryColor/5 shadow-md'
                              : 'border-gray-200 hover:border-primaryColor/60 hover:shadow-md bg-white'
                              }`}
                          >
                            {/* Header Section */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0 pr-2">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <h4 className="text-sm font-bold text-gray-900 truncate">{shift.name}</h4>
                                  <span className="px-2 py-0.5 text-[10px] font-semibold text-green-700 bg-green-50 rounded-full border border-green-200 flex-shrink-0">
                                    Active
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                                  <LocationIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                                  <span className="truncate font-medium">{shift.zone}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                                  <AccessTimeIcon sx={{ fontSize: '12px', color: '#9CA3AF' }} />
                                  <span className="truncate">{formatDateTime(shift.startTime)} - {formatDateTime(shift.endTime)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Capacity Section */}
                            <div className="pt-2 border-t border-gray-100">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-semibold text-gray-600 flex items-center gap-1.5 uppercase tracking-wide">
                                  <PeopleIcon sx={{ fontSize: '12px', color: '#6B7280' }} />
                                  Capacity
                                </span>
                                <span className={`text-xs font-bold ${progressPercentage >= 100
                                  ? 'text-red-600'
                                  : progressPercentage >= 80
                                    ? 'text-amber-600'
                                    : 'text-green-600'
                                  }`}>
                                  {assignedCount} / {maxDrivers}
                                </span>
                              </div>
                              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden shadow-inner mb-1.5">
                                <div
                                  className={`h-full transition-all duration-500 ${progressPercentage >= 100
                                    ? 'bg-gradient-to-r from-red-500 to-red-600'
                                    : progressPercentage >= 80
                                      ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                                      : 'bg-gradient-to-r from-green-500 to-green-600'
                                    }`}
                                  style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                                />
                              </div>
                              <p className="text-[10px] text-gray-500 font-medium">
                                {progressPercentage >= 100
                                  ? 'At full capacity'
                                  : progressPercentage >= 80
                                    ? 'Nearly full'
                                    : `${maxDrivers - assignedCount} spots available`}
                              </p>
                            </div>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
                {hasMoreShifts && (
                  <div className="px-3 py-3 border-t border-gray-200 mt-2">
                    <button
                      onClick={loadMoreShifts}
                      disabled={shiftsLoading || isLoadingMore}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-primaryColor rounded-lg hover:bg-darkBlue disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                    >
                      {isLoadingMore ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Loading more shifts...</span>
                        </>
                      ) : (
                        <span>Load More</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedShift && driversToShow.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col min-h-0 flex-1">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900">Drivers</h3>
                  <span className="text-xs text-gray-500">({driversToShow.length})</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-1.5">
                <div className="space-y-1.5">
                  {driversToShow.map((driver) => {
                    const assignment = shiftAssignments.find(a => a.driverId === driver.id);
                    const assignmentStatus: AssignmentStatus = assignment?.status || 'pending';
                    const isSelected = selectedDriverId === driver.id;
                    const hasLocation = driver.latitude != null && driver.longitude != null;

                    return (
                      <Tooltip
                        key={driver.id}
                        title={hasLocation ? "Click to focus map on this driver" : "No location data available"}
                        arrow
                        placement="left"
                      >
                        <div
                          onClick={() => {
                            if (hasLocation) {
                              setSelectedDriverId(driver.id);
                              setOpenPopupDriverId(driver.id);
                            }
                          }}
                          className={`group rounded-md border cursor-pointer transition-all ${isSelected
                            ? 'border-green-500 bg-green-50 shadow-md px-2.5 py-2 scale-[1.02]'
                            : 'border-gray-200 hover:border-green-400/60 hover:bg-gray-50/80 bg-white px-2 py-1.5'
                            } ${!hasLocation ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              {/* ID and Driver Name in first line */}
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {driver.id && (
                                  <span className="text-xs text-gray-500">
                                    <span className="font-medium">ID</span> <span className="font-mono">{driver.id}</span>
                                  </span>
                                )}
                                {driver.id && (
                                  <span className="text-gray-300">•</span>
                                )}
                                <h4 className="text-sm font-semibold truncate text-gray-900">{driver.name}</h4>
                              </div>

                              {/* Phone Number and Assignment Status in same line */}
                              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                {driver.phone && (
                                  <>
                                    <div className="flex items-center gap-1">
                                      <PhoneIcon sx={{ fontSize: '11px', color: '#6B7280' }} />
                                      <span className="text-xs truncate text-gray-600">{driver.phone}</span>
                                    </div>
                                    <span className="text-gray-300">•</span>
                                  </>
                                )}
                                {assignment && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Assignment:</span>
                                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${getStatusColor(assignmentStatus)}`}>
                                      {assignmentStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Assigned Date */}
                              {assignment && assignment.createdAt && (
                                <div className="text-[9px] text-gray-400 mt-1">
                                  Assigned {formatDate(assignment.createdAt)}
                                </div>
                              )}

                              {/* Latitude and Longitude */}
                              {driver.latitude != null && driver.longitude != null ? (
                                <div className="flex items-center gap-1 mt-1.5">
                                  <LocationIcon sx={{ fontSize: '10px', color: '#6B7280' }} />
                                  <span className="text-[9px] text-gray-600 font-mono">
                                    {driver.latitude.toFixed(4)}, {driver.longitude.toFixed(4)}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 mt-1.5">
                                  <LocationIcon sx={{ fontSize: '10px', color: '#EF4444' }} />
                                  <span className="text-[9px] text-red-600 italic">Location not available</span>
                                </div>
                              )}

                              {/* Status Display */}
                              {(driver.isOnline !== undefined || driver.isBusy !== undefined) && (
                                <div className="flex items-center gap-1 mt-1.5">
                                  {(() => {
                                    if (driver.isOnline === false) {
                                      return (
                                        <>
                                          <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                          <span className="text-[9px] text-gray-600">Offline</span>
                                        </>
                                      );
                                    } else if (driver.isBusy === true) {
                                      return (
                                        <>
                                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                          <span className="text-[9px] text-gray-600">Busy</span>
                                        </>
                                      );
                                    } else if (driver.isOnline === true) {
                                      return (
                                        <>
                                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                          <span className="text-[9px] text-gray-600">Online</span>
                                        </>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              )}
                            </div>

                            {isSelected && (
                              <div className="flex-shrink-0 pt-0.5">
                                <CheckCircleIcon sx={{ fontSize: '14px', color: '#10B981' }} />
                              </div>
                            )}
                          </div>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {selectedShift && driversToShow.length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col min-h-0 flex-1">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900">Drivers</h3>
                  <span className="text-xs text-gray-500">(0)</span>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <PeopleIcon sx={{ fontSize: '32px', color: '#9CA3AF' }} />
                  </div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">No drivers assigned</p>
                  <p className="text-xs text-gray-500">This shift does not contain any drivers</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex-1 min-h-0 relative">
            {!selectedShift && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50/90 to-blue-50/50 z-10 backdrop-blur-sm">
                <div className="text-center p-10 bg-white/95 rounded-2xl border border-gray-200/60 shadow-xl max-w-md mx-4">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mb-4">
                    <MapIcon sx={{ fontSize: '40px', color: '#6366F1' }} />
                  </div>
                  <p className="text-xl font-bold text-gray-800 mb-2">No Shift Session Selected</p>
                  <p className="text-sm text-gray-600 mb-4">Select a shift session from the filters above or choose one from the left panel to start monitoring drivers on the map</p>
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <InfoIcon sx={{ fontSize: '14px' }} />
                    <span>Drivers will appear as colored markers on the map</span>
                  </div>
                </div>
              </div>
            )}
            <MapContainer
              center={[centerLat, centerLng]}
              zoom={zoom}
              zoomControl={true}
              attributionControl={false}
              style={{ height: '100%', width: '100%' }}
            >
              <ReactLeafletGoogleLayer
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                {...({ type: "roadmap", styles: MapStyleRetro } as any)}
              />

              <MapBoundsUpdater
                drivers={driversToShow}
                selectedDriverId={selectedDriverId}
                openPopupDriverId={openPopupDriverId}
              />

              {driversToShow.map((driver) => {
                if (driver.latitude == null || driver.longitude == null) return null;

                return (
                  <MarkerWithRef
                    key={driver.id}
                    driver={driver}
                    selectedDriverId={selectedDriverId}
                    openPopupDriverId={openPopupDriverId}
                    onMarkerClick={handleDriverClick}
                    markerRefs={markerRefs}
                    shiftAssignments={shiftAssignments}
                    activeShifts={activeShifts}
                  />
                );
              })}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
